 
/**
 * S21 · Success — submission receipt. Reads the generated receipt id
 * out of session.meta and displays it alongside a "back to sessions"
 * CTA. The underlying `session.submit` task is already in the sync
 * queue by the time we get here.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import { useCapturesBySession, useSession } from '@kapture/client-core';

export const Route = createFileRoute('/sessions/$sessionId/review/success')({
  component: SuccessPage,
  staticData: { mobile: true },
});

function SuccessPage() {
  const { sessionId } = Route.useParams();
  const { data: session } = useSession(sessionId);
  const { data: captures } = useCapturesBySession(sessionId);

  const receipt = (session?.meta['receipt'] as string | undefined) ?? '—';
  const signerName = (session?.meta['signerName'] as string | undefined) ?? 'Sarah Chen';

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
          fontFamily: T.font,
          gap: 18,
        }}
      >
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 48,
            background: T.goodSoft,
            color: T.good,
            display: 'grid',
            placeItems: 'center',
            boxShadow: '0 8px 30px rgba(10,148,88,0.18)',
          }}
        >
          <Ico d={I.check2} size={44} sw={2.4} stroke={T.good} />
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Submitted
          </div>
          <div style={{ fontSize: 13, color: T.textDim, marginTop: 6, maxWidth: 280, lineHeight: 1.5 }}>
            Your report is queued for sync. It will reach the reviewer as soon as signal returns.
          </div>
        </div>

        <div
          style={{
            width: '100%',
            maxWidth: 340,
            borderRadius: 14,
            border: `1px solid ${T.stroke}`,
            background: T.surface,
            overflow: 'hidden',
          }}
        >
          <ReceiptRow label="Receipt" value={receipt} mono />
          <ReceiptRow label="Session" value={session?.title ?? '—'} />
          <ReceiptRow label="Captures" value={String(captures?.length ?? 0)} />
          <ReceiptRow label="Signed by" value={signerName} last />
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Chip tone="good" size="sm" icon={<Ico d={I.check2} size={11} />}>
            Signed locally
          </Chip>
          <Chip tone="brandSoft" size="sm" icon={<Ico d={I.cloud} size={11} />}>
            Queued for upload
          </Chip>
        </div>
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Link
          to="/sessions/$sessionId/share"
          params={{ sessionId }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '10px 14px',
            height: 40,
            borderRadius: 10,
            border: `1px solid ${T.stroke}`,
            background: T.surface,
            color: T.text,
            fontFamily: T.font,
            fontWeight: 600,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          <Ico d={I.send} size={14} />
          Share
        </Link>
        {/* Straight to the feed that now tracks it, rather than back to a
            list this session has just left. */}
        <Link
          to="/submissions"
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            padding: '10px 16px',
            height: 40,
            background: T.brand,
            color: '#fff',
            border: `1px solid ${T.brand}`,
            borderRadius: 10,
            fontFamily: T.font,
            fontWeight: 600,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Track it
          <Ico d={I.arrow} size={16} />
        </Link>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function ReceiptRow({
  label,
  value,
  mono,
  last,
}: {
  label: string;
  value: string;
  mono?: boolean;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderBottom: last ? 'none' : `1px solid ${T.stroke}`,
      }}
    >
      <div
        style={{
          width: 80,
          fontSize: 10,
          color: T.textMute,
          fontFamily: T.mono,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          textAlign: 'left',
        }}
      >
        {label}
      </div>
      <div
        style={{
          flex: 1,
          fontSize: 13,
          color: T.text,
          fontFamily: mono ? T.mono : T.font,
          fontWeight: mono ? 700 : 500,
          textAlign: 'left',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </div>
    </div>
  );
}
